import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface MentionItem {
  id: string; // 稳定身份："ref:<imgIndex>" | "res:<resId>"
  token: string; // 当前 render 的 "@图片1"，仅用于显示
  kind: 'image' | 'video' | 'audio';
  thumbnailUrl?: string;
  label: string;
}

export interface MentionTrigger {
  start: number; // '@' 字形偏移
  end: number; // 当前 caret（selectionStart）
}

const WS = /\s/;
const EMAIL_LOCAL_CHAR = /[A-Za-z0-9._%+-]/;

// 仅当光标紧贴在 '@' 之后才触发（'@' 与 caret 之间不得有任何字符）：即只在用户刚输入
// '@' 的瞬间弹出。这样光标落在已确认的 @token 内部/之后再打字时不会被误触发——中文提示词里
// '@图片1的特写' 这类 token 紧跟汉字、无空格分隔，旧的"向前扫到最近 @"会一路命中 token 的
// '@' 而反复弹窗。邮箱样式只有在 '@' 前后都是邮箱本地字符时排除，避免英文句尾
// 直接输入 '@' 被误判为邮箱。
export function detectMentionTrigger(text: string, caret: number): MentionTrigger | null {
  if (caret <= 0) return null;
  const i = caret - 1;
  if (text[i] !== '@') return null;
  const prev = i === 0 ? '' : text[i - 1];
  const next = i + 1 < text.length ? text[i + 1] : '';
  if (EMAIL_LOCAL_CHAR.test(prev) && EMAIL_LOCAL_CHAR.test(next)) return null; // 邮箱/英文单词中的 @
  return { start: i, end: caret };
}

// 把 [start,end) 替换为 token + 尾随空格，返回新文本与新 caret。
export function computeInsertion(
  text: string,
  start: number,
  end: number,
  token: string
): { nextText: string; nextCaret: number } {
  const before = text.slice(0, start);
  const after = text.slice(end);
  const inserted = before + token + ' ';
  return { nextText: inserted + after, nextCaret: inserted.length };
}

interface MentionState {
  open: boolean;
  start: number;
  end: number;
  selectedId: string | null;
}

const CLOSED: MentionState = { open: false, start: 0, end: 0, selectedId: null };

export interface UseAtMentionParams {
  items: MentionItem[];
  value: string;
  onChange: (next: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}

export interface UseAtMentionApi {
  open: boolean;
  start: number;
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  confirm: (id?: string) => void;
  close: () => void;
  handleSelectionChange: () => void;
  handleCaretMove: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export function useAtMention({ items, value, onChange, textareaRef }: UseAtMentionParams): UseAtMentionApi {
  const [state, setState] = useState<MentionState>(CLOSED);
  const pendingCaretRef = useRef<number | null>(null);

  const close = useCallback(() => setState((s) => (s.open ? CLOSED : s)), []);

  // 由 caret/文本变化驱动：重新检测触发。closed→open 时选首项；open 中保持有效 selectedId。
  const handleSelectionChange = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? 0;
    const trig = detectMentionTrigger(el.value, caret);
    if (!trig || items.length === 0) {
      close();
      return;
    }
    setState((s) => {
      const keep = s.open && s.selectedId != null && items.some((it) => it.id === s.selectedId);
      return {
        open: true,
        start: trig.start,
        end: trig.end,
        selectedId: keep ? s.selectedId : items[0].id,
      };
    });
  }, [items, textareaRef, close]);

  // 由鼠标点击/选区变化驱动：只维护已打开菜单的区间，不从 closed 状态打开。
  // 打开菜单只允许发生在用户实际输入文本后，避免点击已有 @token 时误弹出。
  const handleCaretMove = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? 0;
    const trig = detectMentionTrigger(el.value, caret);
    setState((s) => {
      if (!s.open) return s;
      if (!trig || trig.start !== s.start) return CLOSED;
      return { ...s, end: trig.end };
    });
  }, [textareaRef]);

  // items 异步变化：菜单开着且 selectedId 已不存在（选中素材被删）→ 关闭，不回退他项。
  useEffect(() => {
    setState((s) => {
      if (!s.open) return s;
      if (s.selectedId != null && items.some((it) => it.id === s.selectedId)) return s;
      return CLOSED;
    });
  }, [items]);

  // 插入后把 caret 落到 token 之后并重聚焦/自适应高度（受控组件，value 变更后再应用）。
  useEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret == null) return;
    pendingCaretRef.current = null;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(caret, caret);
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, textareaRef]);

  const setSelectedId = useCallback((id: string) => {
    setState((s) => (s.open ? { ...s, selectedId: id } : s));
  }, []);

  // 注意：副作用（onChange / 写 ref）必须在 setState 之外，否则 StrictMode 双调用 updater 会触发两次。
  const confirm = useCallback(
    (id?: string) => {
      if (!state.open) return;
      const targetId = id ?? state.selectedId;
      const item = targetId != null ? items.find((it) => it.id === targetId) : undefined;
      if (!item) {
        setState(CLOSED); // 身份已失效：中止插入并关闭
        return;
      }
      const { nextText, nextCaret } = computeInsertion(value, state.start, state.end, item.token);
      pendingCaretRef.current = nextCaret;
      onChange(nextText);
      setState(CLOSED);
    },
    [state, items, value, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!state.open || items.length === 0) return;
      const idx = items.findIndex((it) => it.id === state.selectedId);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = items[(idx < 0 ? 0 : idx + 1) % items.length];
        setSelectedId(next.id);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = items[(idx <= 0 ? items.length - 1 : idx - 1) % items.length];
        setSelectedId(next.id);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        confirm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    },
    [state.open, state.selectedId, items, setSelectedId, confirm, close]
  );

  return {
    open: state.open,
    start: state.start,
    selectedId: state.selectedId,
    setSelectedId,
    confirm,
    close,
    handleSelectionChange,
    handleCaretMove,
    handleKeyDown,
  };
}
